"use client"

import { useState, useEffect } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import "./AddProject.css"
import * as XLSX from "xlsx"
import Papa from "papaparse"
import { fetchWeatherDataFormatted } from "../services/getWeatherData"
const AddProject = () => {
  const navigate = useNavigate()
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const { id } = useParams()
  const location = useLocation()
  const isEditing = !!id

  // Get farm ID from URL params if coming from FarmDetails page
  const urlParams = new URLSearchParams(location.search)
  const preSelectedFarmId = urlParams.get("farmId")

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    farm: preSelectedFarmId || "",
    cropType: "",
    crop: "",
    soilType: "",
    allocatedArea: "",
    plantingDate: new Date().toISOString().split("T")[0],
    harvestDate: "",
    status: "planning",
    parameterGroup: "",
  })

  const [farms, setFarms] = useState([])
  const [cropTypes, setCropTypes] = useState([])
  const [crops, setCrops] = useState([])
  const [soilTypes, setSoilTypes] = useState([])
  const [selectedFarm, setSelectedFarm] = useState(null)
  const [selectedCropType, setSelectedCropType] = useState(null)
  const [selectedCrop, setSelectedCrop] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [useCustomCropType, setUseCustomCropType] = useState(false)
  const [useCustomSoilType, setUseCustomSoilType] = useState(false)
  const [parameterGroups, setParameterGroups] = useState([])
  const [customCropType, setCustomCropType] = useState({
    cropType: "",
    crop: "",
    initial: "20",
    development: "30",
    mid: "40",
    late: "30",
    total: "120",
    region: "",
    kcInitial: "0.3",
    kcMid: "1.0",
    kcEnd: "0.7",
  })
  const [customSoilType, setCustomSoilType] = useState({
    name: "",
    description: "",
    texture: "loamy",
    drainageRate: "moderate",
    waterHoldingCapacity: "medium",
  })
  const [usedArea, setUsedArea] = useState(0)
  const [cropDetails, setCropDetails] = useState(null)
  const [editingCropDetails, setEditingCropDetails] = useState(false)
  const [originalGrowthStages, setOriginalGrowthStages] = useState(null)
  const [savedCropDetails, setSavedCropDetails] = useState(null)
  const [defaultPlantingDate, setDefaultPlantingDate] = useState("")
  const [dateDifference, setDateDifference] = useState(0)

  const [editedCropDetails, setEditedCropDetails] = useState({
    initial: "",
    development: "",
    mid: "",
    late: "",
    total: "",
    kcInitial: "",
    kcMid: "",
    kcEnd: "",
  })

  // Add this state variable at the top with other state declarations
  const [areaCalculationMethod, setAreaCalculationMethod] = useState("direct") // "direct" or "dimensions"
  const [areaDimensions, setAreaDimensions] = useState({
    length: "",
    width: "",
  })

  const [showDataUpload, setShowDataUpload] = useState(false)
  const [deviceDataFiles, setDeviceDataFiles] = useState({})
  const [otherDataFile, setOtherDataFile] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFarmGroup, setSelectedFarmGroup] = useState(null)
  const [uploading, setUploading] = useState(false)

  // Add these states at the top with other state declarations
  const [apiWeatherData, setApiWeatherData] = useState(null)
  const [apiWeatherLoading, setApiWeatherLoading] = useState(false)
  const [apiWeatherError, setApiWeatherError] = useState(null)

  // Selected parameter group details
  const [selectedParameterGroupDetails, setSelectedParameterGroupDetails] = useState(null)
  const [showParameterGroupDetails, setShowParameterGroupDetails] = useState(false)

  // Available parameters with details
  const availableParameters = [
    { id: "tmax", name: "Maximum Temperature", unit: "°C", description: "Daily maximum temperature" },
    { id: "ta", name: "Average Temperature", unit: "°C", description: "Daily average temperature" },
    { id: "tmin", name: "Minimum Temperature", unit: "°C", description: "Daily minimum temperature" },
    { id: "stationFetch", name: "Station Fetch", unit: "km", description: "Distance to weather station" },
    { id: "rhmax", name: "Maximum Relative Humidity", unit: "%", description: "Daily maximum relative humidity" },
    { id: "rha", name: "Average Relative Humidity", unit: "%", description: "Daily average relative humidity" },
    { id: "rhmin", name: "Minimum Relative Humidity", unit: "%", description: "Daily minimum relative humidity" },
    { id: "uz", name: "Wind Speed at Height Z", unit: "m/s", description: "Wind speed at measurement height" },
    { id: "ud", name: "Wind Speed During Day", unit: "m/s", description: "Daytime wind speed" },
    { id: "un", name: "Wind Speed During Night", unit: "m/s", description: "Nighttime wind speed" },
    { id: "zw", name: "Wind Measurement Height", unit: "m", description: "Height of wind measurement" },
    { id: "zp", name: "Psychrometer Height", unit: "m", description: "Height of psychrometer" },
    { id: "n", name: "Sunshine Hours", unit: "hours", description: "Daily sunshine duration" },
    { id: "rs", name: "Solar Radiation", unit: "MJ/m²/day", description: "Daily solar radiation" },
    { id: "rn", name: "Net Radiation", unit: "MJ/m²/day", description: "Daily net radiation" },
    { id: "epan", name: "Pan Evaporation", unit: "mm/day", description: "Daily pan evaporation" },
  ]

  // Convention mapping: apiParam -> fetchWeatherDataFormatted response key
  const apiParamConventionMap = {
    Tmax: "Tmax", // Maximum Temperature (°C)
    Tmin: "Tmin", // Minimum Temperature (°C)
    Ta: "Ta", // Average Temperature (°C)
    RHmax: "RHmax", // Maximum Relative Humidity (%)
    RHmin: "RHmin", // Minimum Relative Humidity (%)
    Uz: "Uz", // Wind Speed (m/s)
    zw: "zw", // Wind Measurement Height (m)
    n: "n", // Actual Sunshine Hours (Hours)
    hc_ref: "hc_ref", // Crop Height (m)
    J: "J", // Day of the Year (-)
    E: "elevation", // Elevation Above Sea Level (m)
    P: "P", // Atmospheric Pressure (kPa)
    LAT: "latitude", // Latitude (Degrees)
    Rn: "Rn", // Net Radiation at Crop Surface (MJ/m²/day)
    Rs: "Rs", // Shortwave Solar Radiation (MJ/m²/day)
  }

  // Utility: Map API params to weather data using direct key match (no convention)
  const getApiDataForUpload = (apiParams, apiWeatherData) => {
    if (!apiParams || !apiWeatherData) return null
    const weather = apiWeatherData.weather
    const location = apiWeatherData.location
    // weather can be an array (interval) or object (single day)
    const weatherObj = Array.isArray(weather) ? weather[0] : weather
    const apiData = {}
    apiParams.forEach((paramId) => {
      // Try to get from weatherObj first, then location (handle array values in location)
      if (weatherObj && Object.prototype.hasOwnProperty.call(weatherObj, paramId)) {
        apiData[paramId] = weatherObj[paramId]
      } else if (location && Object.prototype.hasOwnProperty.call(location, paramId)) {
        // If location[paramId] is an array with one value, use the value directly
        const val = location[paramId]
        apiData[paramId] = Array.isArray(val) && val.length === 1 ? val[0] : val
      }
    })
    return apiData
  }

  // Planting month recommendations
  const getPlantingRecommendations = (cropName) => {
    const recommendations = {
      // Common crops with their optimal planting months
      wheat: { months: [10, 11, 12], season: "Winter" },
      rice: { months: [5, 6, 7], season: "Summer" },
      corn: { months: [3, 4, 5], season: "Spring" },
      tomato: { months: [2, 3, 4, 8, 9], season: "Spring/Fall" },
      potato: { months: [1, 2, 3, 9, 10], season: "Spring/Fall" },
      cotton: { months: [4, 5, 6], season: "Summer" },
      soybean: { months: [4, 5, 6], season: "Summer" },
      barley: { months: [10, 11, 12], season: "Winter" },
      sunflower: { months: [3, 4, 5], season: "Spring" },
      sugarcane: { months: [2, 3, 4], season: "Spring" },
      onion: { months: [10, 11, 12, 1], season: "Winter" },
      carrot: { months: [8, 9, 10], season: "Fall" },
      lettuce: { months: [2, 3, 8, 9, 10], season: "Spring/Fall" },
      cabbage: { months: [8, 9, 10], season: "Fall" },
      pepper: { months: [2, 3, 4], season: "Spring" },
    }

    if (!cropName) return null

    const cropKey = cropName.toLowerCase()
    for (const [key, value] of Object.entries(recommendations)) {
      if (cropKey.includes(key)) {
        return value
      }
    }
    return null
  }

  const calculateProjectDates = (startDate, endDate) => {
    const dates = []
    const start = new Date(startDate)
    const end = new Date(endDate)

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split("T")[0])
    }

    return dates
  }

  // Get default planting date based on crop recommendations
  const getDefaultPlantingDate = (cropName) => {
    const recommendations = getPlantingRecommendations(cropName)
    if (!recommendations || !recommendations.months || recommendations.months.length === 0) {
      return new Date().toISOString().split("T")[0]
    }

    // Get the first recommended month
    const month = recommendations.months[0] - 1 // JavaScript months are 0-indexed
    const currentYear = new Date().getFullYear()

    // Create a date for the first day of the recommended month
    const plantingDate = new Date(currentYear, month, 1)

    // If the recommended month is in the past for this year, use next year
    if (plantingDate < new Date()) {
      plantingDate.setFullYear(currentYear + 1)
    }

    return plantingDate.toISOString().split("T")[0]
  }

  // Calculate date difference in days
  const calculateDateDifference = (selectedDate, defaultDate) => {
    if (!selectedDate || !defaultDate) return 0

    const selected = new Date(selectedDate)
    const defaultD = new Date(defaultDate)
    const diffTime = selected - defaultD
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  }

  // Calculate harvest date based on planting date and total days
  const calculateHarvestDate = (plantingDate, totalDays) => {
    if (!plantingDate || !totalDays) return ""

    const planting = new Date(plantingDate)
    const harvest = new Date(planting)
    harvest.setDate(harvest.getDate() + Number.parseInt(totalDays))

    return harvest.toISOString().split("T")[0]
  }

  // Determine project status based on planting date
  const determineProjectStatus = (plantingDate) => {
    if (!plantingDate) return "planning"

    const today = new Date()
    const planting = new Date(plantingDate)

    if (planting > today) {
      return "planning" // Future date
    } else {
      return "active" // Today or past date
    }
  }

  // Redistribute growth stages proportionally when total days change
  const redistributeGrowthStages = (newTotal, currentStages) => {
    if (!currentStages || !originalGrowthStages) return currentStages

    const originalTotal =
      originalGrowthStages.initial +
      originalGrowthStages.development +
      originalGrowthStages.mid +
      originalGrowthStages.late

    if (originalTotal === 0) return currentStages

    // Calculate proportions based on original stages
    const proportions = {
      initial: originalGrowthStages.initial / originalTotal,
      development: originalGrowthStages.development / originalTotal,
      mid: originalGrowthStages.mid / originalTotal,
      late: originalGrowthStages.late / originalTotal,
    }

    // Redistribute based on proportions
    const newStages = {
      initial: Math.round(newTotal * proportions.initial),
      development: Math.round(newTotal * proportions.development),
      mid: Math.round(newTotal * proportions.mid),
      late: Math.round(newTotal * proportions.late),
    }

    // Adjust for rounding errors
    const calculatedTotal = newStages.initial + newStages.development + newStages.mid + newStages.late
    const difference = newTotal - calculatedTotal

    if (difference !== 0) {
      // Add/subtract the difference to the largest stage
      const largest = Object.keys(newStages).reduce((a, b) => (newStages[a] > newStages[b] ? a : b))
      newStages[largest] += difference
    }

    return newStages
  }

  // Status options
  const statusOptions = [
    { value: "planning", label: t("Planning") },
    { value: "active", label: t("Active") },
    { value: "completed", label: t("Completed") },
    { value: "cancelled", label: t("Cancelled") },
  ]

  // Month names for recommendations
  const monthNames = [
    t("January"),
    t("February"),
    t("March"),
    t("April"),
    t("May"),
    t("June"),
    t("July"),
    t("August"),
    t("September"),
    t("October"),
    t("November"),
    t("December"),
  ]

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        const [farmsRes, cropTypesRes, soilTypesRes] = await Promise.all([
          axios.get(`${API_URL}/api/farms`),
          axios.get(`${API_URL}/api/crop-types`),
          axios.get(`${API_URL}/api/soil-types`),
        ])

        setFarms(farmsRes.data)
        setCropTypes(cropTypesRes.data)
        setSoilTypes(soilTypesRes.data)
        // Fetch parameter groups
        const groupsRes = await axios.get(`${API_URL}/api/groups`)
        setParameterGroups(groupsRes.data)
        // If there's a pre-selected farm, set it up
        if (preSelectedFarmId && !isEditing) {
          const preSelectedFarm = farmsRes.data.find((f) => f._id === preSelectedFarmId)
          if (preSelectedFarm) {
            setSelectedFarm(preSelectedFarm)
            setFormData((prev) => ({ ...prev, farm: preSelectedFarmId }))

            // Get used area for the pre-selected farm
            try {
              const farmProjectsRes = await axios.get(`${API_URL}/api/projects/farm/${preSelectedFarmId}`)
              const farmProjects = farmProjectsRes.data
              const totalUsedArea = farmProjects.reduce((sum, project) => sum + project.allocatedArea, 0)
              setUsedArea(totalUsedArea)
            } catch (err) {
              console.error("Error fetching farm Fields:", err)
            }
          }
        }

        if (isEditing) {
          const projectRes = await axios.get(`${API_URL}/api/projects/${id}`)
          const project = projectRes.data

          // Set selected farm
          setSelectedFarm(project.farm)

          // Get used area for the farm
          const farmProjectsRes = await axios.get(`${API_URL}/api/projects/farm/${project.farm._id}`)
          const farmProjects = farmProjectsRes.data
          const totalUsedArea = farmProjects
            .filter((p) => p._id !== project._id)
            .reduce((sum, p) => sum + p.allocatedArea, 0)
          setUsedArea(totalUsedArea)

          // Determine if using custom crop type
          if (project.customCropType) {
            setUseCustomCropType(true)
            setCustomCropType({
              cropType: project.customCropType.cropType || "",
              crop: project.customCropType.crop || "",
              initial: project.customCropType.growthStages.initial.toString(),
              development: project.customCropType.growthStages.development.toString(),
              mid: project.customCropType.growthStages.mid.toString(),
              late: project.customCropType.growthStages.late.toString(),
              total: project.customCropType.total.toString(),
              region: project.customCropType.region || "",
              kcInitial: project.customCropType.cropCoefficients.initial.toString(),
              kcMid: project.customCropType.cropCoefficients.mid.toString(),
              kcEnd: project.customCropType.cropCoefficients.end.toString(),
            })
          } else if (project.cropType) {
            // Using existing crop type
            setFormData((prev) => ({ ...prev, cropType: project.cropType._id }))
            setSelectedCropType(project.cropType)

            // Fetch crops for this crop type
            if (project.cropType._id) {
              const cropsRes = await axios.get(`${API_URL}/api/crops/by-crop-type/${project.cropType._id}`)
              setCrops(cropsRes.data)

              // Set selected crop if it exists
              if (project.crop) {
                setFormData((prev) => ({ ...prev, crop: project.crop._id }))
                setSelectedCrop(project.crop)
                setCropDetails(project.crop)

                // Store original growth stages for proportional redistribution
                setOriginalGrowthStages({
                  initial: project.crop.growthStages.initialDays,
                  development: project.crop.growthStages.developmentDays,
                  mid: project.crop.growthStages.midSeasonDays,
                  late: project.crop.growthStages.lateSeasonDays,
                })

                setEditedCropDetails({
                  initial: project.crop.growthStages.initialDays.toString(),
                  development: project.crop.growthStages.developmentDays.toString(),
                  mid: project.crop.growthStages.midSeasonDays.toString(),
                  late: project.crop.growthStages.lateSeasonDays.toString(),
                  total: project.crop.totalDays.toString(),
                  kcInitial: project.crop.cropCoefficients.initial.toString(),
                  kcMid: project.crop.cropCoefficients.mid.toString(),
                  kcEnd: project.crop.cropCoefficients.end.toString(),
                })

                // Check if there are saved crop details
                if (project.cropDetails) {
                  setSavedCropDetails(project.cropDetails)
                }
              }
            }
          }

          // Determine if using custom soil type
          if (project.customSoilType) {
            setUseCustomSoilType(true)
            setCustomSoilType({
              name: project.customSoilType.name || "",
              description: project.customSoilType.description || "",
              texture: project.customSoilType.properties?.texture || "loamy",
              drainageRate: project.customSoilType.properties?.drainageRate || "moderate",
              waterHoldingCapacity: project.customSoilType.properties?.waterHoldingCapacity || "medium",
            })
          } else if (project.soilType) {
            // Using existing soil type
            setFormData((prev) => ({ ...prev, soilType: project.soilType._id }))
          }

          // Set form data
          setFormData((prev) => ({
            ...prev,
            name: project.name,
            description: project.description || "",
            farm: project.farm._id,
            allocatedArea: project.allocatedArea.toString(),
            plantingDate: new Date(project.plantingDate).toISOString().split("T")[0],
            harvestDate: project.harvestDate ? new Date(project.harvestDate).toISOString().split("T")[0] : "",
            status: project.status || "planning",
          }))
        }

        setLoading(false)
      } catch (err) {
        console.error(err)
        setError(t("Failed to load data"))
        setLoading(false)
      }
    }

    fetchData()
  }, [id, isEditing, t, preSelectedFarmId])

  useEffect(() => {
    if (selectedFarm && selectedFarm.parameterGroup) {
      // Fetch the parameter group details
      const fetchParameterGroup = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/groups/${selectedFarm.parameterGroup}`)
          setSelectedFarmGroup(response.data)
        } catch (err) {
          console.error("Error fetching parameter Station:", err)
        }
      }
      fetchParameterGroup()
    }
  }, [selectedFarm])

  // Auto-calculate harvest date when planting date or total days change
  useEffect(() => {
    if (formData.plantingDate) {
      let totalDays = 0

      if (useCustomCropType) {
        totalDays = Number.parseInt(customCropType.total) || 0
      } else if (editedCropDetails.total) {
        totalDays = Number.parseInt(editedCropDetails.total) || 0
      } else if (cropDetails) {
        totalDays = cropDetails.totalDays || 0
      }

      if (totalDays > 0) {
        const calculatedHarvestDate = calculateHarvestDate(formData.plantingDate, totalDays)
        setFormData((prev) => ({ ...prev, harvestDate: calculatedHarvestDate }))
      }

      // Update project status based on planting date
      const newStatus = determineProjectStatus(formData.plantingDate)
      setFormData((prev) => ({ ...prev, status: newStatus }))

      // Calculate date difference if there's a default date
      if (defaultPlantingDate) {
        const diff = calculateDateDifference(formData.plantingDate, defaultPlantingDate)
        setDateDifference(diff)
      }
    }
  }, [
    formData.plantingDate,
    customCropType.total,
    editedCropDetails.total,
    cropDetails,
    useCustomCropType,
    defaultPlantingDate,
  ])

  const handleFarmChange = async (e) => {
    const farmId = e.target.value
    setFormData({ ...formData, farm: farmId, allocatedArea: "" })

    if (farmId) {
      try {
        // Get farm details
        const farm = farms.find((f) => f._id === farmId)
        setSelectedFarm(farm)

        // Get used area for the farm
        const farmProjectsRes = await axios.get(`${API_URL}/api/projects/farm/${farmId}`)
        const farmProjects = farmProjectsRes.data
        const totalUsedArea = farmProjects.reduce((sum, project) => sum + project.allocatedArea, 0)
        setUsedArea(totalUsedArea)
      } catch (err) {
        console.error(err)
        showNotification(t("Failed to fetch farm details"), "error")
      }
    } else {
      setSelectedFarm(null)
      setUsedArea(0)
      setApiWeatherData(null)
    }
  }

  const handleCropTypeChange = async (e) => {
    const cropTypeId = e.target.value
    setFormData({ ...formData, cropType: cropTypeId, crop: "" })
    setCropDetails(null)
    setEditingCropDetails(false)
    setOriginalGrowthStages(null)
    setSavedCropDetails(null)

    if (cropTypeId) {
      try {
        // Get crop type details
        const cropType = cropTypes.find((ct) => ct._id === cropTypeId)
        setSelectedCropType(cropType)

        // Fetch crops for this crop type
        const cropsRes = await axios.get(`${API_URL}/api/crops/by-crop-type/${cropTypeId}`)
        setCrops(cropsRes.data)

        // If using custom crop type, populate the form with crop type details
        if (useCustomCropType) {
          setCustomCropType((prev) => ({
            ...prev,
            cropType: cropType.name,
          }))
        }
      } catch (err) {
        console.error(err)
        showNotification(t("Failed to fetch crop type details"), "error")
      }
    } else {
      setSelectedCropType(null)
      setCrops([])
    }
  }

  const handleCropChange = async (e) => {
    const cropId = e.target.value
    setFormData({ ...formData, crop: cropId })

    if (cropId) {
      try {
        // Get crop details
        const cropRes = await axios.get(`${API_URL}/api/crops/${cropId}`)
        const crop = cropRes.data
        setSelectedCrop(crop)
        setCropDetails(crop)
        setSavedCropDetails(null)

        // Store original growth stages for proportional redistribution
        setOriginalGrowthStages({
          initial: crop.growthStages.initialDays,
          development: crop.growthStages.developmentDays,
          mid: crop.growthStages.midSeasonDays,
          late: crop.growthStages.lateSeasonDays,
        })

        // Initialize edited crop details
        setEditedCropDetails({
          initial: crop.growthStages.initialDays.toString(),
          development: crop.growthStages.developmentDays.toString(),
          mid: crop.growthStages.midSeasonDays.toString(),
          late: crop.growthStages.lateSeasonDays.toString(),
          total: crop.totalDays.toString(),
          kcInitial: crop.cropCoefficients.initial.toString(),
          kcMid: crop.cropCoefficients.mid.toString(),
          kcEnd: crop.cropCoefficients.end.toString(),
        })

        // Set default planting date based on crop recommendations
        const defaultDate = getDefaultPlantingDate(crop.name)
        setDefaultPlantingDate(defaultDate)

        // Only set the planting date if it's not already set (for new projects)
        if (!isEditing) {
          setFormData((prev) => ({
            ...prev,
            plantingDate: defaultDate,
            status: determineProjectStatus(defaultDate),
          }))
          setDateDifference(0) // Reset difference for default date
        }

        // --- Fetch weather data if farm and planting/harvest dates are available ---
        if (selectedFarm && selectedFarm.location && Array.isArray(selectedFarm.location.coordinates)) {
          const [longitude, latitude] = selectedFarm.location.coordinates
          if ((formData.plantingDate || defaultDate) && (formData.harvestDate || crop.totalDays)) {
            const plantingDate = formData.plantingDate || defaultDate
            // Calculate harvest date if not set
            const harvestDate = formData.harvestDate || calculateHarvestDate(plantingDate, crop.totalDays)
            setApiWeatherLoading(true)
            setApiWeatherError(null)
            try {
              const result = await fetchWeatherDataFormatted({
                latitude: Number.parseFloat(latitude),
                longitude: Number.parseFloat(longitude),
                startDate: plantingDate,
                endDate: new Date(new Date(plantingDate).setDate(new Date(plantingDate).getDate() + 7))
                  .toISOString()
                  .split("T")[0],
                forecastDays: 0, // Use historical data only
              })
              console.log("Fetched weather data:", result)
              setApiWeatherData(result)
            } catch (err) {
              setApiWeatherError("Failed to fetch weather data for farm location")
              setApiWeatherData(null)
            } finally {
              setApiWeatherLoading(false)
            }
          }
        }
        // --- end fetch weather data ---
      } catch (err) {
        console.error(err)
        showNotification(t("Failed to fetch crop details"), "error")
      }
    } else {
      setSelectedCrop(null)
      setCropDetails(null)
      setOriginalGrowthStages(null)
      setSavedCropDetails(null)
      setDefaultPlantingDate("")
      setDateDifference(0)
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleCustomCropTypeChange = (e) => {
    const { name, value } = e.target
    setCustomCropType({ ...customCropType, [name]: value })

    // Calculate total days if changing growth stages
    if (["initial", "development", "mid", "late"].includes(name)) {
      const updatedValues = { ...customCropType, [name]: value }
      const total = (
        Number.parseInt(updatedValues.initial) +
        Number.parseInt(updatedValues.development) +
        Number.parseInt(updatedValues.mid) +
        Number.parseInt(updatedValues.late)
      ).toString()
      setCustomCropType((prev) => ({ ...prev, [name]: value, total }))
    }
  }

  const handleCustomSoilTypeChange = (e) => {
    setCustomSoilType({ ...customSoilType, [e.target.name]: e.target.value })
  }

  const handleEditedCropDetailsChange = (e) => {
    const { name, value } = e.target

    if (name === "total") {
      // When total days change, redistribute growth stages proportionally
      const newTotal = Number.parseInt(value) || 0
      const currentStages = {
        initial: Number.parseInt(editedCropDetails.initial) || 0,
        development: Number.parseInt(editedCropDetails.development) || 0,
        mid: Number.parseInt(editedCropDetails.mid) || 0,
        late: Number.parseInt(editedCropDetails.late) || 0,
      }

      const redistributed = redistributeGrowthStages(newTotal, currentStages)

      setEditedCropDetails({
        ...editedCropDetails,
        total: value,
        initial: redistributed.initial.toString(),
        development: redistributed.development.toString(),
        mid: redistributed.mid.toString(),
        late: redistributed.late.toString(),
      })
    } else if (["initial", "development", "mid", "late"].includes(name)) {
      // When individual stages change, update total
      const updatedDetails = { ...editedCropDetails, [name]: value }
      const total = (
        Number.parseInt(updatedDetails.initial) +
        Number.parseInt(updatedDetails.development) +
        Number.parseInt(updatedDetails.mid) +
        Number.parseInt(updatedDetails.late)
      ).toString()

      setEditedCropDetails({ ...updatedDetails, total })
    } else {
      setEditedCropDetails({ ...editedCropDetails, [name]: value })
    }
  }

  const toggleEditCropDetails = () => {
    setEditingCropDetails(!editingCropDetails)
  }

  const saveCropDetails = () => {
    // Create a new crop details object based on edited values
    const newCropDetails = {
      ...cropDetails,
      growthStages: {
        initialDays: Number.parseInt(editedCropDetails.initial),
        developmentDays: Number.parseInt(editedCropDetails.development),
        midSeasonDays: Number.parseInt(editedCropDetails.mid),
        lateSeasonDays: Number.parseInt(editedCropDetails.late),
      },
      totalDays: Number.parseInt(editedCropDetails.total),
      cropCoefficients: {
        initial: Number.parseFloat(editedCropDetails.kcInitial),
        mid: Number.parseFloat(editedCropDetails.kcMid),
        end: Number.parseFloat(editedCropDetails.kcEnd),
      },
    }

    // Save the edited crop details
    setSavedCropDetails(newCropDetails)

    // Update the crop details in the form
    setCropDetails(newCropDetails)

    // Exit editing mode
    setEditingCropDetails(false)

    // Recalculate harvest date based on new total days
    if (formData.plantingDate) {
      const calculatedHarvestDate = calculateHarvestDate(
        formData.plantingDate,
        Number.parseInt(editedCropDetails.total),
      )
      setFormData((prev) => ({ ...prev, harvestDate: calculatedHarvestDate }))
    }

    showNotification(t("Crop details saved successfully"), "success")
  }

  const handleCustomCropTypeToggle = (useCustom) => {
    setUseCustomCropType(useCustom)
    if (useCustom) {
      // Reset form data for crop type selection
      setFormData((prev) => ({ ...prev, cropType: "", crop: "" }))
      setSelectedCropType(null)
      setSelectedCrop(null)
      setCropDetails(null)
      setCrops([])
      setOriginalGrowthStages(null)
      setSavedCropDetails(null)
      setDefaultPlantingDate("")
      setDateDifference(0)
    } else {
      // Reset custom crop type data
      setCustomCropType({
        cropType: "",
        crop: "",
        initial: "20",
        development: "30",
        mid: "40",
        late: "30",
        total: "120",
        region: "",
        kcInitial: "0.3",
        kcMid: "1.0",
        kcEnd: "0.7",
      })
    }
  }

  const createCustomCrop = async () => {
    try {
      if (!selectedCropType) {
        showNotification(t("Please select a crop type first"), "error")
        return
      }

      const cropData = {
        cropType: selectedCropType._id,
        name: customCropType.crop,
        description: `Custom crop created for Field: ${formData.name}`,
        region: customCropType.region,
        growthStages: {
          initialDays: Number.parseInt(customCropType.initial),
          developmentDays: Number.parseInt(customCropType.development),
          midSeasonDays: Number.parseInt(customCropType.mid),
          lateSeasonDays: Number.parseInt(customCropType.late),
        },
        totalDays: Number.parseInt(customCropType.total),
        cropCoefficients: {
          initial: Number.parseFloat(customCropType.kcInitial),
          mid: Number.parseFloat(customCropType.kcMid),
          end: Number.parseFloat(customCropType.kcEnd),
        },
      }

      const response = await axios.post(`${API_URL}/api/crops`, cropData)
      const newCrop = response.data

      // Add the new crop to the crops list and select it
      setCrops((prev) => [...prev, newCrop])
      setFormData((prev) => ({ ...prev, crop: newCrop._id }))
      setSelectedCrop(newCrop)
      setCropDetails(newCrop)

      // Switch back to regular mode
      setUseCustomCropType(false)

      showNotification(t("Custom crop created successfully"), "success")
    } catch (err) {
      console.error(err)
      showNotification(err.response?.data?.msg || t("Failed to create custom crop"), "error")
    }
  }

  const generateSampleExcelFile = (parameters, fileName) => {
    // Create sample data
    const sampleData = []
    const today = new Date()

    // Generate 7 days of sample data
    for (let i = 0; i < 7; i++) {
      const date = new Date(today)
      date.setDate(date.getDate() + i)

      const row = {
        Date: date.toISOString().split("T")[0],
      }

      // Add sample values for each parameter
      parameters.forEach((param) => {
        const paramInfo = availableParameters.find((p) => p.id === param)
        if (paramInfo) {
          // Generate realistic sample values based on parameter type
          switch (param) {
            case "tmax":
              row[param] = (25 + Math.random() * 10).toFixed(1) // 25-35°C
              break
            case "ta":
              row[param] = (20 + Math.random() * 10).toFixed(1) // 20-30°C
              break
            case "tmin":
              row[param] = (15 + Math.random() * 8).toFixed(1) // 15-23°C
              break
            case "rhmax":
              row[param] = (70 + Math.random() * 25).toFixed(1) // 70-95%
              break
            case "rha":
              row[param] = (50 + Math.random() * 30).toFixed(1) // 50-80%
              break
            case "rhmin":
              row[param] = (30 + Math.random() * 25).toFixed(1) // 30-55%
              break
            case "uz":
            case "ud":
            case "un":
              row[param] = (1 + Math.random() * 4).toFixed(1) // 1-5 m/s
              break
            case "n":
              row[param] = (6 + Math.random() * 6).toFixed(1) // 6-12 hours
              break
            case "rs":
              row[param] = (15 + Math.random() * 15).toFixed(1) // 15-30 MJ/m²/day
              break
            case "rn":
              row[param] = (10 + Math.random() * 10).toFixed(1) // 10-20 MJ/m²/day
              break
            case "epan":
              row[param] = (3 + Math.random() * 5).toFixed(1) // 3-8 mm/day
              break
            case "zw":
              row[param] = "10" // Standard 10m height
              break
            case "zp":
              row[param] = "2" // Standard 2m height
              break
            case "stationFetch":
              row[param] = (1 + Math.random() * 10).toFixed(1) // 1-11 km
              break
            default:
              row[param] = (Math.random() * 10).toFixed(2)
          }
        }
      })

      sampleData.push(row)
    }

    // Convert to CSV format
    const headers = ["Date", ...parameters]
    const csvContent = [
      headers.join(","),
      ...sampleData.map((row) => headers.map((header) => row[header] || "").join(",")),
    ].join("\n")

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute("download", fileName)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadDeviceSampleFile = (device) => {
    const fileName = `sample_${device.name.replace(/[^a-zA-Z0-9]/g, "_")}_data.csv`
    generateSampleExcelFile(device.selectedParameters || [], fileName)
  }

  const downloadOtherDataSampleFile = () => {
    const deviceParams = selectedFarmGroup.selectedDevices?.flatMap((d) => d.selectedParameters || []) || []
    const apiParams = selectedFarmGroup.apiParams || []
    const allUsedParams = [...deviceParams, ...apiParams]
    const remainingParams = availableParameters.filter((p) => !allUsedParams.includes(p.id)).map((p) => p.id)

    const fileName = "sample_other_parameters_data.csv"
    generateSampleExcelFile(remainingParams, fileName)
  }

  const handleDeviceFileChange = (deviceId, file) => {
    if (file) {
      setDeviceDataFiles((prev) => ({
        ...prev,
        [deviceId]: file,
      }))
    } else {
      // Remove file if no file selected
      setDeviceDataFiles((prev) => {
        const updated = { ...prev }
        delete updated[deviceId]
        return updated
      })
    }
  }

  const handleOtherDataFileChange = (file) => {
    setOtherDataFile(file)
  }

  // Utility: Parse file (csv/xls/xlsx) to JSON
  const parseFileToJson = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      const ext = file.name.split(".").pop().toLowerCase()
      reader.onload = (e) => {
        try {
          if (ext === "csv") {
            Papa.parse(e.target.result, {
              header: true,
              skipEmptyLines: true,
              complete: (results) => resolve(results.data),
              error: (err) => reject(err),
            })
          } else if (ext === "xls" || ext === "xlsx") {
            const workbook = XLSX.read(e.target.result, { type: "binary" })
            const sheetName = workbook.SheetNames[0]
            const sheet = workbook.Sheets[sheetName]
            const json = XLSX.utils.sheet_to_json(sheet, { defval: "" })
            resolve(json)
          } else {
            reject(new Error("Unsupported file type"))
          }
        } catch (err) {
          reject(err)
        }
      }
      if (ext === "csv") {
        reader.readAsText(file)
      } else if (ext === "xls" || ext === "xlsx") {
        reader.readAsBinaryString(file)
      } else {
        reject(new Error("Unsupported file type"))
      }
    })
  }

  // Refactored: Handle data upload on client, convert to JSON, then upload
  const handleDataUpload = async () => {
    // if (
    //   Object.keys(deviceDataFiles).length === 0 &&
    //   !otherDataFile &&
    //   !(selectedFarmGroup?.apiParams?.length && apiWeatherData)
    // ) {
    //   showNotification(t("Please select at least one file to upload"), "error")
    //   return
    // }

    showNotification(t("Uploaded Data"), "info")
    setUploading(true)
    setUploadProgress(0)

    try {
      // 1. Parse all files to JSON
      const deviceDataJson = {}
      const totalFiles = Object.keys(deviceDataFiles).length + (otherDataFile ? 1 : 0)
      let processedFiles = 0

      // Parse device files
      for (const [deviceId, file] of Object.entries(deviceDataFiles)) {
        // eslint-disable-next-line no-await-in-loop
        const jsonData = await parseFileToJson(file)
        deviceDataJson[deviceId] = jsonData
        processedFiles++
        setUploadProgress(Math.round((processedFiles / totalFiles) * 100))
      }

      // Parse other data file
      let otherDataJson = null
      if (otherDataFile) {
        otherDataJson = await parseFileToJson(otherDataFile)
        processedFiles++
        setUploadProgress(Math.round((processedFiles / totalFiles) * 100))
      }

      // 2. Prepare API data if needed (only send params in apiParams)
      let apiDataJson = null
      if (selectedFarmGroup?.apiParams?.length && apiWeatherData) {
        apiDataJson = getApiDataForUpload(selectedFarmGroup.apiParams, apiWeatherData)
      }

      // 3. Prepare payload
      const payload = {
        groupId: selectedFarmGroup._id,
        groupName: selectedFarmGroup.name,
        deviceData: deviceDataJson,
        otherData: otherDataJson,
        apiData: apiDataJson,
      }

      // 4. Send JSON to API
      await axios.post(`${API_URL}/api/weather-data/upload`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      })

      showNotification(t("Weather data uploaded successfully"), "success")
      setDeviceDataFiles({})
      setOtherDataFile(null)
      setUploadProgress(0)
    } catch (err) {
      console.error("Upload error:", err)
      showNotification(err.response?.data?.msg || t("Failed to upload weather data"), "error")
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Prepare project data
      const projectData = {
        name: formData.name,
        description: formData.description,
        farm: formData.farm,
        allocatedArea: Number.parseFloat(formData.allocatedArea),
        plantingDate: formData.plantingDate,
        parameterGroup: formData.parameterGroup, // Ensure parameterGroup is included
        harvestDate: formData.harvestDate || null,
        status: formData.status,
      }

      // Add crop type and crop data
      if (useCustomCropType) {
        projectData.customCropType = {
          cropType: customCropType.cropType,
          crop: customCropType.crop,
          growthStages: {
            initial: Number.parseInt(customCropType.initial),
            development: Number.parseInt(customCropType.development),
            mid: Number.parseInt(customCropType.mid),
            late: Number.parseInt(customCropType.late),
          },
          total: Number.parseInt(customCropType.total),
          region: customCropType.region,
          cropCoefficients: {
            initial: Number.parseFloat(customCropType.kcInitial),
            mid: Number.parseFloat(customCropType.kcMid),
            end: Number.parseFloat(customCropType.kcEnd),
          },
        }
      } else {
        projectData.cropType = formData.cropType

        if (formData.crop) {
          projectData.crop = formData.crop

          // If crop details were saved, include them
          if (savedCropDetails) {
            projectData.cropDetails = savedCropDetails
          }
          // Otherwise, if crop details were edited, include the edited values
          else if (editingCropDetails) {
            projectData.cropOverrides = {
              growthStages: {
                initial: Number.parseInt(editedCropDetails.initial),
                development: Number.parseInt(editedCropDetails.development),
                mid: Number.parseInt(editedCropDetails.mid),
                late: Number.parseInt(editedCropDetails.late),
              },
              totalDays: Number.parseInt(editedCropDetails.total),
              cropCoefficients: {
                initial: Number.parseFloat(editedCropDetails.kcInitial),
                mid: Number.parseFloat(editedCropDetails.kcMid),
                end: Number.parseFloat(editedCropDetails.kcEnd),
              },
            }
          }
        }
      }

      // Add soil type data
      if (useCustomSoilType) {
        projectData.customSoilType = {
          name: customSoilType.name,
          description: customSoilType.description,
          properties: {
            texture: customSoilType.texture,
            drainageRate: customSoilType.drainageRate,
            waterHoldingCapacity: customSoilType.waterHoldingCapacity,
          },
        }
      } else {
        projectData.soilType = formData.soilType
      }

      if (savedCropDetails) {
        projectData.cropDetails = savedCropDetails
      } else {
        projectData.cropDetails = cropDetails
      }

      if (isEditing) {
        await axios.put(`${API_URL}/api/projects/${id}`, projectData)
        showNotification(t("Field updated successfully"), "success")

        // Recalculate if crop details changed
        if (projectData.crop || projectData.cropDetails) {
          try {
            await axios.post(`${API_URL}/api/projects/${id}/recalculate`)
            showNotification(t("Calculations updated"), "success")
          } catch (calcError) {
            console.error("Error recalculating:", calcError)
          }
        }
      } else {
        const response = await axios.post(`${API_URL}/api/projects`, projectData)
        const newProjectId = response.data._id
        showNotification(t("Field created successfully"), "success")

        // Auto-calculate WATref and ETplant for new projects
        try {
          await axios.post(`${API_URL}/api/projects/${newProjectId}/calculate-etref`)
          await axios.post(`${API_URL}/api/projects/${newProjectId}/calculate-etplant`)
          showNotification(t("Initial calculations completed"), "success")
        } catch (calcError) {
          console.error("Error with initial calculations:", calcError)
        }
      }

      // Navigate back to the appropriate page
      if (preSelectedFarmId) {
        navigate(`/farms/${preSelectedFarmId}`)
      } else {
        navigate("/dashboard")
      }
    } catch (err) {
      console.error(err)
      showNotification(err.response?.data?.msg || t("Failed to save Field"), "error")
      setLoading(false)
    }
  }

  const getAvailableArea = () => {
    if (!selectedFarm) return 0
    return selectedFarm.area - usedArea
  }

  // Add this function to handle area calculation method change
  const handleAreaMethodChange = (method) => {
    setAreaCalculationMethod(method)

    // Reset area fields when changing method
    setFormData({
      ...formData,
      allocatedArea: "",
    })
    setAreaDimensions({
      length: "",
      width: "",
    })
  }

  // Add this function to handle dimension changes
  const handleDimensionChange = (e) => {
    const { name, value } = e.target
    const newDimensions = { ...areaDimensions, [name]: value }
    setAreaDimensions(newDimensions)

    // Calculate area if both length and width are provided
    if (newDimensions.length && newDimensions.width) {
      const area = Number.parseFloat(newDimensions.length) * Number.parseFloat(newDimensions.width)
      setFormData({
        ...formData,
        allocatedArea: area.toFixed(2),
      })
    }
  }

  // Add this function to handle parameter group selection
  const handleParameterGroupChange = async (e) => {
    const groupId = e.target.value
    setFormData({ ...formData, parameterGroup: groupId })

    if (groupId) {
      try {
        const response = await axios.get(`${API_URL}/api/groups/${groupId}`)
        setSelectedParameterGroupDetails(response.data)
        setShowParameterGroupDetails(true) // Automatically show details when a group is selected
      } catch (err) {
        console.error("Error fetching parameter group details:", err)
        showNotification(t("Failed to fetch parameter group details"), "error")
      }
    } else {
      setSelectedParameterGroupDetails(null)
      setShowParameterGroupDetails(false)
    }
  }

  // Add this function to toggle the visibility of parameter group details
  const toggleParameterGroupDetails = () => {
    setShowParameterGroupDetails((prev) => !prev)
  }

  if (loading && !farms.length) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t("Loading...")}</p>
      </div>
    )
  }

  // Get planting recommendations for selected crop
  const plantingRecommendations = selectedCrop ? getPlantingRecommendations(selectedCrop.name) : null

  // Format date difference message
  const getDateDifferenceMessage = () => {
    if (dateDifference === 0) return ""

    if (dateDifference > 0) {
      return t(`You added ${dateDifference} days to the default date`)
    } else {
      return t(`You are ${Math.abs(dateDifference)} days before the default date`)
    }
  }

  return (
    <div className="add-project-container" dir={direction}>
      <div className="add-project-header">
        <h1>{isEditing ? t("Edit Field") : t("Add New Field")}</h1>
        <p className="subtitle">
          {preSelectedFarmId && selectedFarm
            ? t(`Creating Field for ${selectedFarm.name}`)
            : t("Manage your crop Fields")}
        </p>
      </div>

      {error && (
        <div className="alert-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <div className="add-project-card">
        <form onSubmit={handleSubmit} className="project-form">
          <div className="form-section">
            <h2 className="section-title">{t("Field Details")}</h2>
            <div className="form-group">
              <label htmlFor="name">
                {t("Field Name")}
                <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">{t("Description")}</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-control"
                rows="3"
              ></textarea>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">{t("Field Status")}</label>
                {/* <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-control"
                  required
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select> */}
                <small className="form-text text-muted">
                  {t("Status is automatically determined based on planting date")}
                </small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">{t("Farm Selection")}</h2>
            <div className="form-group">
              <label htmlFor="farm">
                {t("Select Farm")}
                <span style={{ color: "red" }}>*</span>
              </label>
              <select
                id="farm"
                name="farm"
                value={formData.farm}
                onChange={handleFarmChange}
                className="form-control"
                required
                disabled={!!preSelectedFarmId && !isEditing}
              >
                <option value="">{t("Select a farm")}</option>
                {farms.map((farm) => (
                  <option key={farm._id} value={farm._id}>
                    {farm.name}
                  </option>
                ))}
              </select>
              {preSelectedFarmId && selectedFarm && (
                <small className="form-text text-success">
                  <i className="fas fa-check-circle"></i> {t("Farm automatically selected from previous page")}
                </small>
              )}
            </div>

            {selectedFarm && (
              <div className="farm-info">
                <div className="info-item">
                  <span className="info-label">{t("Total Farm Area")}:</span>
                  <span className="info-value">{selectedFarm.area} m²</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t("Used Area")}:</span>
                  <span className="info-value">{usedArea} m²</span>
                </div>
                <div className="info-item">
                  <span className="info-label">{t("Available Area")}:</span>
                  <span className="info-value">{getAvailableArea()} m²</span>
                </div>
              </div>
            )}

            {selectedFarm && (
              <div className="crop-area-section">
                <div className="area-calculation-toggle">
                  <button
                    type="button"
                    className={`toggle-btn ${areaCalculationMethod === "direct" ? "active" : ""}`}
                    onClick={() => handleAreaMethodChange("direct")}
                  >
                    {t("Enter Area Directly")}
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${areaCalculationMethod === "dimensions" ? "active" : ""}`}
                    onClick={() => handleAreaMethodChange("dimensions")}
                  >
                    {t("Calculate from Length × Width")}
                  </button>
                </div>

                {areaCalculationMethod === "direct" ? (
                  <div className="form-group">
                    <label htmlFor="allocatedArea">{t("Crop Area (m²)")}</label>
                    <input
                      type="number"
                      id="allocatedArea"
                      name="allocatedArea"
                      value={formData.allocatedArea}
                      onChange={handleChange}
                      className="form-control"
                      min="0"
                      max={getAvailableArea() + (isEditing ? Number.parseFloat(formData.allocatedArea) : 0)}
                      step="0.01"
                      required
                    />
                    {Number.parseFloat(formData.allocatedArea) >
                      getAvailableArea() + (isEditing ? Number.parseFloat(formData.allocatedArea) : 0) && (
                      <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i> {t("Crop area exceeds available area")}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="dimensions-inputs">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="length">{t("Length (meters)")}</label>
                        <input
                          type="number"
                          id="length"
                          name="length"
                          value={areaDimensions.length}
                          onChange={handleDimensionChange}
                          className="form-control"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="width">{t("Width (meters)")}</label>
                        <input
                          type="number"
                          id="width"
                          name="width"
                          value={areaDimensions.width}
                          onChange={handleDimensionChange}
                          className="form-control"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="calculated-area">{t("Calculated Area (m²)")}</label>
                      <input
                        type="text"
                        id="calculated-area"
                        value={formData.allocatedArea}
                        className="form-control"
                        readOnly
                        disabled
                      />
                    </div>
                    {Number.parseFloat(formData.allocatedArea) >
                      getAvailableArea() + (isEditing ? Number.parseFloat(formData.allocatedArea) : 0) && (
                      <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i> {t("Crop area exceeds available area")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <h2 className="section-title">
              {t("Crop Type")}
              <span style={{ color: "red" }}>*</span>
            </h2>

            <div className="source-toggle">
              <button
                type="button"
                className={`toggle-btn ${!useCustomCropType ? "active" : ""}`}
                onClick={() => handleCustomCropTypeToggle(false)}
              >
                {t("Use Existing Crop")}
              </button>
              <button
                type="button"
                className={`toggle-btn ${useCustomCropType ? "active" : ""}`}
                onClick={() => handleCustomCropTypeToggle(true)}
              >
                {t("Create Custom Crop")}
              </button>
            </div>

            {!useCustomCropType && (
              <div className="crop-selection">
                <div className="form-group">
                  <label htmlFor="cropType">{t("Select Crop Type")}</label>
                  <select
                    id="cropType"
                    name="cropType"
                    value={formData.cropType}
                    onChange={handleCropTypeChange}
                    className="form-control"
                    required={!useCustomCropType}
                  >
                    <option value="">{t("Select a crop type")}</option>
                    {cropTypes.map((cropType) => (
                      <option key={cropType._id} value={cropType._id}>
                        {cropType.name} ({cropType.code})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCropType && (
                  <div className="form-group">
                    <label htmlFor="crop">{t("Select Crop")}</label>
                    <select
                      id="crop"
                      name="crop"
                      value={formData.crop}
                      onChange={handleCropChange}
                      className="form-control"
                      required={!useCustomCropType}
                    >
                      <option value="">{t("Select a crop")}</option>
                      {crops.map((crop) => {
                        const date = new Date(0, crop.plantMonth - 1, 1)
                        const monthName = date.toLocaleString("default", { month: "short" })

                        return (
                          <option key={crop._id} value={crop._id}>
                            {crop.name} ({crop.region}) ({crop.totalDays} {t("days")}) ({t("Start Date")}: 1 {monthName}
                            )
                          </option>
                        )
                      })}
                    </select>
                  </div>
                )}

                {/* Planting Recommendations */}
                {plantingRecommendations && (
                  <div className="planting-recommendations">
                    <h4 className="recommendation-title">
                      <i className="fas fa-lightbulb"></i> {t("Planting Recommendations")}
                    </h4>
                    <div className="recommendation-content">
                      <div className="recommendation-item">
                        <span className="recommendation-label">{t("Best Season")}:</span>
                        <span className="recommendation-value">{plantingRecommendations.season}</span>
                      </div>
                      <div className="recommendation-item">
                        <span className="recommendation-label">{t("Recommended Months")}:</span>
                        <span className="recommendation-value">
                          {plantingRecommendations.months.map((month) => monthNames[month - 1]).join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {cropDetails && (
                  <div className="crop-details-section">
                    <div className="crop-details-header">
                      <h3>{t("Crop Details")}</h3>
                      <button type="button" className="edit-button" onClick={toggleEditCropDetails}>
                        {editingCropDetails ? t("Cancel Editing") : t("Edit Details")}
                      </button>
                    </div>

                    {!editingCropDetails ? (
                      <div className="crop-details">
                        <div className="details-row">
                          <div className="detail-item">
                            <span className="detail-label">{t("Crop Name")}:</span>
                            <span className="detail-value">{cropDetails.name}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">{t("Region")}:</span>
                            <span className="detail-value">{cropDetails.region || t("Not specified")}</span>
                          </div>
                        </div>

                        <h4 className="subsection-title">{t("Growth Stages (days)")}</h4>
                        <div className="details-row">
                          <div className="detail-item">
                            <span className="detail-label">{t("Initial")}:</span>
                            <span className="detail-value">{cropDetails.growthStages.initialDays}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">{t("Development")}:</span>
                            <span className="detail-value">{cropDetails.growthStages.developmentDays}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">{t("Mid")}:</span>
                            <span className="detail-value">{cropDetails.growthStages.midSeasonDays}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">{t("Late")}:</span>
                            <span className="detail-value">{cropDetails.growthStages.lateSeasonDays}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">{t("Total")}:</span>
                            <span className="detail-value">{cropDetails.totalDays}</span>
                          </div>
                        </div>

                        <h4 className="subsection-title">{t("Crop Coefficients")}</h4>
                        <div className="details-row">
                          <div className="detail-item">
                            <span className="detail-label">{t("Kc ini")}:</span>
                            <span className="detail-value">{cropDetails.cropCoefficients.initial}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">{t("Kc mid")}:</span>
                            <span className="detail-value">{cropDetails.cropCoefficients.mid}</span>
                          </div>
                          <div className="detail-item">
                            <span className="detail-label">{t("Kc end")}:</span>
                            <span className="detail-value">{cropDetails.cropCoefficients.end}</span>
                          </div>
                        </div>

                        {savedCropDetails && (
                          <div className="saved-notification">
                            <i className="fas fa-check-circle"></i> {t("Custom crop details saved")}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="crop-details-edit">
                        <div className="form-group">
                          <label htmlFor="totalDays">{t("Total Days")}</label>
                          <input
                            type="number"
                            id="totalDays"
                            name="total"
                            value={editedCropDetails.total}
                            onChange={handleEditedCropDetailsChange}
                            className="form-control"
                            min="0"
                            required
                          />
                          <small className="form-text text-muted">
                            {t("Changing total days will automatically redistribute growth stages proportionally")}
                          </small>
                        </div>

                        <h4 className="subsection-title">{t("Growth Stages (days)")}</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="initial">{t("Initial")}</label>
                            <input
                              type="number"
                              id="initial"
                              name="initial"
                              value={editedCropDetails.initial}
                              onChange={handleEditedCropDetailsChange}
                              className="form-control"
                              min="0"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="development">{t("Development")}</label>
                            <input
                              type="number"
                              id="development"
                              name="development"
                              value={editedCropDetails.development}
                              onChange={handleEditedCropDetailsChange}
                              className="form-control"
                              min="0"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="mid">{t("Mid")}</label>
                            <input
                              type="number"
                              id="mid"
                              name="mid"
                              value={editedCropDetails.mid}
                              onChange={handleEditedCropDetailsChange}
                              className="form-control"
                              min="0"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="late">{t("Late")}</label>
                            <input
                              type="number"
                              id="late"
                              name="late"
                              value={editedCropDetails.late}
                              onChange={handleEditedCropDetailsChange}
                              className="form-control"
                              min="0"
                              required
                            />
                          </div>
                        </div>

                        <h4 className="subsection-title">{t("Crop Coefficients")}</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="kcInitial">{t("Kc ini")}</label>
                            <input
                              type="number"
                              id="kcInitial"
                              name="kcInitial"
                              value={editedCropDetails.kcInitial}
                              onChange={handleEditedCropDetailsChange}
                              className="form-control"
                              min="0"
                              max="2"
                              step="0.01"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="kcMid">{t("Kc mid")}</label>
                            <input
                              type="number"
                              id="kcMid"
                              name="kcMid"
                              value={editedCropDetails.kcMid}
                              onChange={handleEditedCropDetailsChange}
                              className="form-control"
                              min="0"
                              max="2"
                              step="0.01"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="kcEnd">{t("Kc end")}</label>
                            <input
                              type="number"
                              id="kcEnd"
                              name="kcEnd"
                              value={editedCropDetails.kcEnd}
                              onChange={handleEditedCropDetailsChange}
                              className="form-control"
                              min="0"
                              max="2"
                              step="0.01"
                              required
                            />
                          </div>
                        </div>

                        <div className="edit-actions">
                          <button type="button" className="cancel-edit-button" onClick={toggleEditCropDetails}>
                            {t("Cancel")}
                          </button>
                          <button type="button" className="save-details-button" onClick={saveCropDetails}>
                            {t("Save Changes")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {useCustomCropType && (
              <div className="custom-crop-type-form">
                <div className="form-group">
                  <label htmlFor="baseCropType">{t("Base Crop Type")}</label>
                  <select
                    id="baseCropType"
                    name="cropType"
                    value={formData.cropType}
                    onChange={handleCropTypeChange}
                    className="form-control"
                    required={useCustomCropType}
                  >
                    <option value="">{t("Select a base crop type")}</option>
                    {cropTypes.map((cropType) => (
                      <option key={cropType._id} value={cropType._id}>
                        {cropType.name} ({cropType.code})
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    {t("Select the crop type category for your custom crop")}
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="crop">{t("Custom Crop Name")}</label>
                    <input
                      type="text"
                      id="crop"
                      name="crop"
                      value={customCropType.crop}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      required={useCustomCropType}
                      placeholder={t("Enter your custom crop name")}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="region">{t("Region")}</label>
                    <input
                      type="text"
                      id="region"
                      name="region"
                      value={customCropType.region}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      placeholder={t("e.g., Mediterranean, Tropical")}
                    />
                  </div>
                </div>

                <h3 className="subsection-title">{t("Growth Stages (days)")}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="initial">{t("Initial (Lini)")}</label>
                    <input
                      type="number"
                      id="initial"
                      name="initial"
                      value={customCropType.initial}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      min="0"
                      required={useCustomCropType}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="development">{t("Development (Ldev)")}</label>
                    <input
                      type="number"
                      id="development"
                      name="development"
                      value={customCropType.development}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      min="0"
                      required={useCustomCropType}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mid">{t("Mid (Lmid)")}</label>
                    <input
                      type="number"
                      id="mid"
                      name="mid"
                      value={customCropType.mid}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      min="0"
                      required={useCustomCropType}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="late">{t("Late (Llate)")}</label>
                    <input
                      type="number"
                      id="late"
                      name="late"
                      value={customCropType.late}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      min="0"
                      required={useCustomCropType}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="total">{t("Total Days")}</label>
                  <input
                    type="number"
                    id="total"
                    name="total"
                    value={customCropType.total}
                    className="form-control"
                    readOnly
                    disabled
                  />
                </div>

                <h3 className="subsection-title">{t("Crop Coefficients")}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="kcInitial">{t("Kc ini")}</label>
                    <input
                      type="number"
                      id="kcInitial"
                      name="kcInitial"
                      value={customCropType.kcInitial}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      min="0"
                      max="2"
                      step="0.01"
                      required={useCustomCropType}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="kcMid">{t("Kc mid")}</label>
                    <input
                      type="number"
                      id="kcMid"
                      name="kcMid"
                      value={customCropType.kcMid}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      min="0"
                      max="2"
                      step="0.01"
                      required={useCustomCropType}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="kcEnd">{t("Kc end")}</label>
                    <input
                      type="number"
                      id="kcEnd"
                      name="kcEnd"
                      value={customCropType.kcEnd}
                      onChange={handleCustomCropTypeChange}
                      className="form-control"
                      min="0"
                      max="2"
                      step="0.01"
                      required={useCustomCropType}
                    />
                  </div>
                </div>

                {selectedCropType && customCropType.crop && (
                  <div className="custom-crop-actions">
                    <button type="button" className="create-crop-button" onClick={createCustomCrop}>
                      <i className="fas fa-plus"></i> {t("Create Custom Crop")}
                    </button>
                    <small className="form-text text-muted">
                      {t("This will create a new crop in the database that you can reuse in other Fields")}
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <h2 className="section-title">{t("Field Timeline")}</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="plantingDate">{t("Planting Date")}</label>
                <input
                  type="date"
                  id="plantingDate"
                  name="plantingDate"
                  value={formData.plantingDate}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
                <small className="form-text text-muted">
                  {selectedCrop && defaultPlantingDate ? (
                    <>
                      {t("Default date is set to the first day of the recommended planting month")}
                      {dateDifference !== 0 && (
                        <div className="date-difference-message">
                          <i className="fas fa-info-circle"></i> {getDateDifferenceMessage()}
                        </div>
                      )}
                    </>
                  ) : (
                    t("Select a crop to see recommended planting date")
                  )}
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="harvestDate">{t("Expected Harvest Date")}</label>
                <input
                  type="date"
                  id="harvestDate"
                  name="harvestDate"
                  value={formData.harvestDate}
                  onChange={handleChange}
                  className="form-control"
                  min={formData.plantingDate}
                />
                <small className="form-text text-muted">
                  {formData.harvestDate
                    ? t("Auto-calculated based on crop growth period")
                    : t("Will be calculated automatically when crop is selected")}
                </small>
              </div>
            </div>
          </div>

          {/* Weather Data Upload Section */}
          {selectedFarmGroup && (
            <div className="form-section">
              <h2 className="section-title">{t("Weather Data Upload")}</h2>
              <p className="section-description">
                {t("Upload weather data files for your parameter group")} - {selectedFarmGroup.name}
              </p>

              {/* Device Parameters Data Upload */}
              {selectedFarmGroup.selectedDevices && selectedFarmGroup.selectedDevices.length > 0 && (
                <div className="devices-upload-section">
                  <h3 className="subsection-title">{t("Weather Parameter Device Upload")}</h3>
                  <p className="upload-description">
                    {t("Upload Excel files for each device with their respective parameters")}
                  </p>

                  <div className="devices-grid">
                    {selectedFarmGroup.selectedDevices.map((device) => (
                      <div key={device._id} className="device-upload-card">
                        <div className="device-header">
                          <h4 className="device-name">
                            <i className="fas fa-microchip"></i>
                            {device.name}
                          </h4>
                          <span className="device-code">{device.deviceCode}</span>
                        </div>

                        <div className="device-info">
                          <div className="device-detail">
                            <span className="detail-label">{t("Height")}:</span>
                            <span className="detail-value">{device.height}m</span>
                          </div>
                          <div className="device-detail">
                            <span className="detail-label">{t("Location")}:</span>
                            <span className="detail-value">
                              {device.location?.coordinates?.[1]?.toFixed(4)},{" "}
                              {device.location?.coordinates?.[0]?.toFixed(4)}
                            </span>
                          </div>
                        </div>

                        <div className="device-parameters">
                          <h5 className="parameters-title">{t("Parameters")}:</h5>
                          <div className="parameters-list">
                            {device.selectedParameters?.map((param) => {
                              const paramInfo = availableParameters.find((p) => p.id === param)
                              return (
                                <span key={param} className="param-badge device-param">
                                  {paramInfo?.name || param}
                                  <small>({paramInfo?.unit || ""})</small>
                                </span>
                              )
                            })}
                          </div>
                        </div>

                        <div className="file-upload-section">
                          <div className="upload-actions-row">
                            <div className="file-upload-group">
                              <label htmlFor={`file-input-${device._id}`} className="file-upload-label">
                                <i className="fas fa-upload"></i> {t("Upload Excel File")}
                              </label>
                              <input
                                id={`file-input-${device._id}`}
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => handleDeviceFileChange(device._id, e.target.files[0])}
                                className="file-input"
                                style={{ display: "none" }}
                              />
                            </div>

                            <button
                              type="button"
                              className="download-sample-btn"
                              onClick={() => downloadDeviceSampleFile(device)}
                              title={t("Download sample file")}
                            >
                              <i className="fas fa-download"></i> {t("Sample")}
                            </button>
                          </div>

                          {deviceDataFiles[device._id] && (
                            <div className="file-selected">
                              <i className="fas fa-file-excel"></i>
                              <span>{deviceDataFiles[device._id].name}</span>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => handleDeviceFileChange(device._id, null)}
                                title={t("Remove file")}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          )}

                          <small className="file-requirements">
                            {t("Excel/CSV file should contain columns: Date, ")}
                            {device.selectedParameters?.join(", ")}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Parameters Data Upload */}
              {(() => {
                const deviceParams = selectedFarmGroup.selectedDevices?.flatMap((d) => d.selectedParameters || []) || []
                const apiParams = selectedFarmGroup.apiParams || []
                const allUsedParams = [...deviceParams, ...apiParams]
                const remainingParams = availableParameters.filter((p) => !allUsedParams.includes(p.id))

                return (
                  remainingParams.length > 0 && (
                    <div className="other-params-upload-section">
                      <h3 className="subsection-title">{t("Other Parameters Data")}</h3>
                      <p className="upload-description">{t("Upload Excel file with remaining parameter data")}</p>

                      <div className="other-params-card">
                        <div className="remaining-parameters">
                          <h5 className="parameters-title">{t("Required Parameters")}:</h5>
                          <div className="parameters-list">
                            {remainingParams.map((param) => (
                              <span key={param.id} className="param-badge other-param">
                                {param.name}
                                <small>({param.unit})</small>
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="file-upload-section">
                          <div className="upload-actions-row">
                            <div className="file-upload-group">
                              <label htmlFor="other-data-file" className="file-upload-label">
                                <i className="fas fa-upload"></i> {t("Upload Excel File")}
                              </label>
                              <input
                                id="other-data-file"
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={(e) => handleOtherDataFileChange(e.target.files[0])}
                                className="file-input"
                                style={{ display: "none" }}
                              />
                            </div>

                            <button
                              type="button"
                              className="download-sample-btn"
                              onClick={downloadOtherDataSampleFile}
                              title={t("Download sample file")}
                            >
                              <i className="fas fa-download"></i> {t("Sample")}
                            </button>
                          </div>

                          {otherDataFile && (
                            <div className="file-selected">
                              <i className="fas fa-file-excel"></i>
                              <span>{otherDataFile.name}</span>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => handleOtherDataFileChange(null)}
                                title={t("Remove file")}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          )}

                          <small className="file-requirements">
                            {t("Excel/CSV file should contain columns: Date, ")}
                            {remainingParams.map((p) => p.id).join(", ")}
                          </small>
                        </div>
                      </div>
                    </div>
                  )
                )
              })()}

              {/* API Parameters Info */}
              {selectedFarmGroup.apiParams && selectedFarmGroup.apiParams.length > 0 && (
                <div className="api-params-section">
                  <h3 className="subsection-title">{t("Forecasting Parameters")}</h3>
                  <div className="api-info-card">
                    <div className="api-info-header">
                      <i className="fas fa-cloud-download-alt"></i>
                      <span>{t("Automatically fetched from weather APIs")}</span>
                    </div>
                    <div className="api-parameters">
                      <div className="parameters-list">
                        {selectedFarmGroup.apiParams.map((param) => {
                          const paramInfo = availableParameters.find((p) => p.id === param)
                          return (
                            <span key={param} className="param-badge api-param">
                              {paramInfo?.name || param}
                              <small>({paramInfo?.unit || ""})</small>
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Actions */}
              <div className="upload-actions">
                <button
                  type="button"
                  className="upload-data-button"
                  onClick={handleDataUpload}
                  disabled={uploading || (Object.keys(deviceDataFiles).length === 0 && !otherDataFile)}
                >
                  {uploading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {t("Uploading...")} {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt"></i>
                      {t("Upload Weather Data")}
                    </>
                  )}
                </button>

                {uploadProgress > 0 && uploading && (
                  <div className="upload-progress">
                    <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="farm-section">
            <h2 className="section-title">{t("Weather Parameters")}</h2>

            <div className="form-group">
              <label htmlFor="parameterGroup">
                {t("Parameter Station")} <span style={{ color: "red" }}>*</span>
              </label>
              <select
                id="parameterGroup"
                name="parameterGroup"
                value={formData.parameterGroup}
                onChange={handleParameterGroupChange}
                className="form-control"
              >
                <option value="">{t("Select a parameter Station")}</option>
                {parameterGroups.map((group) => (
                  <option key={group._id} value={group._id}>
                    {group.name} - {group.groupId}
                  </option>
                ))}
              </select>
              <small className="form-text">
                {t("Select a parameter Station to use for weather data calculations for this farm")}
              </small>
            </div>

            {selectedParameterGroupDetails && (
              <div className="parameter-group-details">
                <button type="button" className="toggle-details-button" onClick={toggleParameterGroupDetails}>
                  {showParameterGroupDetails ? t("Hide Details") : t("Show Details")}
                </button>

                {showParameterGroupDetails && (
                  <div className="details-content">
                    <h3>{t("Parameter Station Details")}</h3>
                    <p>
                      <strong>{t("Name")}:</strong> {selectedParameterGroupDetails.name}
                    </p>
                    <p>
                      <strong>{t("Group ID")}:</strong> {selectedParameterGroupDetails.groupId}
                    </p>
                    <p>
                      <strong>{t("Description")}:</strong>{" "}
                      {selectedParameterGroupDetails.description || t("No description available")}
                    </p>
                    <h4>{t("Parameters")}</h4>
                    <ul>
                      {selectedParameterGroupDetails && (
                        <div className="parameter-group-details">
                          <button type="button" className="toggle-details-button" onClick={toggleParameterGroupDetails}>
                            {showParameterGroupDetails ? t("Hide Details") : t("Show Details")}
                          </button>

                          {showParameterGroupDetails && (
                            <div className="details-content">
                              <h3>{t("Parameter Station Details")}</h3>
                              <p>
                                <strong>{t("Name")}:</strong>{" "}
                                {selectedParameterGroupDetails.name || t("No name available")}
                              </p>
                              <p>
                                <strong>{t("Group ID")}:</strong>{" "}
                                {selectedParameterGroupDetails.groupId || t("No group ID available")}
                              </p>
                              <p>
                                <strong>{t("Location")}:</strong>{" "}
                                {selectedParameterGroupDetails.location?.coordinates?.length === 2
                                  ? `${selectedParameterGroupDetails.location.coordinates[1].toFixed(4)}, ${selectedParameterGroupDetails.location.coordinates[0].toFixed(4)}`
                                  : t("No location available")}
                              </p>
                              <h4>{t("Selected Devices")}</h4>
                              <ul>
                                {selectedParameterGroupDetails.selectedDevices?.length > 0 ? (
                                  selectedParameterGroupDetails.selectedDevices.map((device) => (
                                    <li key={device._id}>
                                      <p>
                                        <strong>{t("Device Name")}:</strong> {device.name}
                                      </p>
                                      <p>
                                        <strong>{t("Device Code")}:</strong> {device.code}
                                      </p>
                                      <p>
                                        <strong>{t("Height")}:</strong> {device.height} m
                                      </p>
                                      <p>
                                        <strong>{t("Active")}:</strong> {device.active ? t("Yes") : t("No")}
                                      </p>
                                      <p>
                                        <strong>{t("Created At")}:</strong>{" "}
                                        {new Date(device.createdAt).toLocaleString()}
                                      </p>
                                      <p>
                                        <strong>{t("Updated At")}:</strong>{" "}
                                        {new Date(device.updatedAt).toLocaleString()}
                                      </p>
                                      <h5>{t("Selected Parameters")}</h5>
                                      <ul>
                                        {device.selectedParameters?.length > 0 ? (
                                          device.selectedParameters.map((param) => <li key={param}>{param}</li>)
                                        ) : (
                                          <li>{t("No parameters selected")}</li>
                                        )}
                                      </ul>
                                    </li>
                                  ))
                                ) : (
                                  <li>{t("No devices selected")}</li>
                                )}
                              </ul>
                              <h4>{t("Forecasting Parameters")}</h4>
                              <ul>
                                {selectedParameterGroupDetails.apiParams?.length > 0 ? (
                                  selectedParameterGroupDetails.apiParams.map((param) => <li key={param}>{param}</li>)
                                ) : (
                                  <li>{t("No API parameters available")}</li>
                                )}
                              </ul>
                              <p>
                                <strong>{t("Active")}:</strong>{" "}
                                {selectedParameterGroupDetails.active ? t("Yes") : t("No")}
                              </p>
                              <p>
                                <strong>{t("Created At")}:</strong>{" "}
                                {new Date(selectedParameterGroupDetails.createdAt).toLocaleString() ||
                                  t("No creation date available")}
                              </p>
                              <p>
                                <strong>{t("Updated At")}:</strong>{" "}
                                {new Date(selectedParameterGroupDetails.updatedAt).toLocaleString() ||
                                  t("No update date available")}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <h2 className="section-title">
              {t("Soil Type")}
              <span style={{ color: "red" }}>*</span>
            </h2>

            <div className="source-toggle">
              <button
                type="button"
                className={`toggle-btn ${!useCustomSoilType ? "active" : ""}`}
                onClick={() => setUseCustomSoilType(false)}
              >
                {t("Use Admin Soil Type")}
              </button>
              <button
                type="button"
                className={`toggle-btn ${useCustomSoilType ? "active" : ""}`}
                onClick={() => setUseCustomSoilType(true)}
              >
                {t("Custom Soil Type")}
              </button>
            </div>

            {!useCustomSoilType && (
              <div className="form-group">
                <label htmlFor="adminSoilType">{t("Select Soil Type")}</label>
                <select
                  id="adminSoilType"
                  name="soilType"
                  value={formData.soilType}
                  onChange={handleChange}
                  className="form-control"
                  required={!useCustomSoilType}
                >
                  <option value="">{t("Select a soil type")}</option>
                  {soilTypes.map((soilType) => (
                    <option key={soilType._id} value={soilType._id}>
                      {soilType.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {useCustomSoilType && (
              <div className="custom-soil-type-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="soilName">{t("Soil Name")}</label>
                    <input
                      type="text"
                      id="soilName"
                      name="name"
                      value={customSoilType.name}
                      onChange={handleCustomSoilTypeChange}
                      className="form-control"
                      required={useCustomSoilType}
                      placeholder={t("e.g., Sandy Loam, Clay")}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="soilDescription">{t("Description")}</label>
                    <input
                      type="text"
                      id="soilDescription"
                      name="description"
                      value={customSoilType.description}
                      onChange={handleCustomSoilTypeChange}
                      className="form-control"
                      placeholder={t("Brief description of the soil")}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="texture">{t("Texture")}</label>
                    <select
                      id="texture"
                      name="texture"
                      value={customSoilType.texture}
                      onChange={handleCustomSoilTypeChange}
                      className="form-control"
                      required={useCustomSoilType}
                    >
                      <option value="sandy">{t("Sandy")}</option>
                      <option value="loamy">{t("Loamy")}</option>
                      <option value="clay">{t("Clay")}</option>
                      <option value="silty">{t("Silty")}</option>
                      <option value="peaty">{t("Peaty")}</option>
                      <option value="chalky">{t("Chalky")}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="drainageRate">{t("Drainage Rate")}</label>
                    <select
                      id="drainageRate"
                      name="drainageRate"
                      value={customSoilType.drainageRate}
                      onChange={handleCustomSoilTypeChange}
                      className="form-control"
                      required={useCustomSoilType}
                    >
                      <option value="very slow">{t("Very Slow")}</option>
                      <option value="slow">{t("Slow")}</option>
                      <option value="moderate">{t("Moderate")}</option>
                      <option value="fast">{t("Fast")}</option>
                      <option value="very fast">{t("Very Fast")}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="waterHoldingCapacity">{t("Water Holding Capacity")}</label>
                    <select
                      id="waterHoldingCapacity"
                      name="waterHoldingCapacity"
                      value={customSoilType.waterHoldingCapacity}
                      onChange={handleCustomSoilTypeChange}
                      className="form-control"
                      required={useCustomSoilType}
                    >
                      <option value="very low">{t("Very Low")}</option>
                      <option value="low">{t("Low")}</option>
                      <option value="medium">{t("Medium")}</option>
                      <option value="high">{t("High")}</option>
                      <option value="very high">{t("Very High")}</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate(preSelectedFarmId ? `/farms/${preSelectedFarmId}` : "/dashboard")}
            >
              {t("Cancel")}
            </button>
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? t("Saving...") : isEditing ? t("Update Field") : t("Create Field")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddProject
